import type {
  LegislativeStatus,
  LegislativeNextStep,
  LegislativeCommittee,
  LegislativeLeadCommittee,
  LifecycleStageTransition,
} from '../types/lifecycle';
import {
  formatDate,
  legislativeStageIndex,
  legislativeConfidenceClass,
  legislativeConfidenceLabel,
  committeeRoleClass,
  committeeRoleLabel,
  LEGISLATIVE_STAGES,
} from '../utils/lifecycleFormat';

// Visual contract: docs/proposals/mockup_legislativni_cesta.html, rendered in
// the existing dashboard style. The legislative path is informational progress,
// not a risk verdict: it uses blue/neutral styling only.
//
// Every field is optional/null-tolerant. A missing value renders as
// "neznamo"/"nepublikovano", never as an assumed date, committee or stage.

function ConfidenceBadge({ confidence }: { confidence: string | null | undefined }) {
  return (
    <span className={`legi-conf ${legislativeConfidenceClass(confidence)}`}>
      {legislativeConfidenceLabel(confidence)}
    </span>
  );
}

/** Resolve the lead (garancni) committee from explicit field or committee roles. */
function resolveLead(status: LegislativeStatus): LegislativeLeadCommittee | null {
  if (status.lead_committee?.name) return status.lead_committee;
  const garancni = (status.committees ?? []).find((c) => c.role === 'garancni');
  return garancni ? { code: garancni.code, name: garancni.name } : null;
}

// Card-level pieces ---------------------------------------------------------

/**
 * Controlled stage badge: prefers legislative_status.stage_label, falling back
 * to current_stage_label only when legislative_status is missing.
 */
export function LegislativeStageBadge({
  status,
  fallbackLabel,
}: {
  status: LegislativeStatus | null | undefined;
  fallbackLabel: string | null | undefined;
}) {
  const label = status?.stage_label ?? (status ? null : fallbackLabel);
  if (!label) return null;
  return <span className="badge badge--stage">{label}</span>;
}

/** "Po 1. cteni" / "Pred 1. ctenim" - omitted when first_reading_done is unknown. */
export function FirstReadingBadge({ status }: { status: LegislativeStatus | null | undefined }) {
  const done = status?.first_reading_done;
  if (done === true) {
    return (
      <span className="badge badge--reading">
        <span className="legi-dot legi-dot--done" aria-hidden="true" /> Po 1. čtení
      </span>
    );
  }
  if (done === false) {
    return (
      <span className="badge badge--reading badge--reading-pending">
        <span className="legi-dot legi-dot--todo" aria-hidden="true" /> Před 1. čtením
      </span>
    );
  }
  return null;
}

/** Lead committee chip, blue/neutral. Omitted when no garancni committee known. */
export function LeadCommitteeChip({ status }: { status: LegislativeStatus | null | undefined }) {
  if (!status) return null;
  const lead = resolveLead(status);
  if (!lead?.name) return null;
  return (
    <span className="badge badge--lead" title="Garanční výbor">
      <span aria-hidden="true">⚖</span> {lead.name}
    </span>
  );
}

/** Compact mini stepper of chamber stages; neutral when stage is unknown. */
export function LegislativeMiniStepper({ status }: { status: LegislativeStatus | null | undefined }) {
  const idx = legislativeStageIndex(status?.stage);
  const segments = LEGISLATIVE_STAGES.slice(0, 6);
  return (
    <div className="legi-mini" aria-hidden="true">
      {segments.map((stage, i) => {
        const cls = idx < 0 ? '' : i < idx ? 'legi-mini__seg--done' : i === idx ? 'legi-mini__seg--now' : '';
        return <span key={stage} className={`legi-mini__seg ${cls}`} />;
      })}
    </div>
  );
}

/** One-line "next step" summary for cards. */
export function NextStepLine({ nextStep }: { nextStep: LegislativeNextStep | null | undefined }) {
  if (nextStep?.date) {
    return (
      <div className="legi-next-line">
        <span className="legi-next-line__lab">Další jednání:</span>{' '}
        {nextStep.committee && <span className="legi-next-line__v">{nextStep.committee} — </span>}
        <span className="legi-next-line__v">{formatDate(nextStep.date)}</span>{' '}
        <ConfidenceBadge confidence={nextStep.date_confidence ?? 'confirmed'} />
      </div>
    );
  }
  if (nextStep?.label) {
    return (
      <div className="legi-next-line">
        <span className="legi-next-line__lab">Další krok:</span>{' '}
        <span className="legi-next-line__v">{nextStep.label}</span>{' '}
        <ConfidenceBadge confidence={nextStep.date_confidence ?? 'expected'} />{' '}
        <span className="legi-next-line__hint">termín zatím neznámý</span>
      </div>
    );
  }
  return (
    <div className="legi-next-line">
      <span className="legi-next-line__lab">Další krok:</span>{' '}
      <span className="legi-next-line__hint">zatím neznámý</span> <ConfidenceBadge confidence="unknown" />
    </div>
  );
}

// Detail-level panels -------------------------------------------------------

function CommitteeRow({ committee }: { committee: LegislativeCommittee }) {
  return (
    <div className="committee-row">
      <div>
        <div className="committee-row__name">{committee.name}</div>
        <div className="committee-row__meta">
          kód {committee.code}
          {committee.assigned_at && <> · přiděleno {formatDate(committee.assigned_at)}</>}
        </div>
      </div>
      <span className={`committee-role ${committeeRoleClass(committee.role)}`}>
        {committeeRoleLabel(committee.role)}
      </span>
    </div>
  );
}

function CommitteeMiniRows({ committees }: { committees: LegislativeCommittee[] | undefined }) {
  if (!committees || committees.length === 0) return null;
  return (
    <div className="legi-timeline__committees">
      {committees.map((committee) => (
        <div className="legi-timeline__committee" key={`${committee.code}-${committee.role}`}>
          <span className={`committee-role ${committeeRoleClass(committee.role)}`}>{committeeRoleLabel(committee.role)}</span>
          <span>{committee.name}</span>
        </div>
      ))}
    </div>
  );
}

function NextStepDetail({ nextStep }: { nextStep: LegislativeNextStep | null | undefined }) {
  if (nextStep?.date) {
    return (
      <div className="legi-next">
        <div className="legi-next__main">
          {nextStep.committee && <span className="legi-next__committee">{nextStep.committee} · </span>}
          {nextStep.label && <span className="legi-next__label">{nextStep.label} </span>}
          <span className="legi-next__date">{formatDate(nextStep.date)}</span>{' '}
          <ConfidenceBadge confidence={nextStep.date_confidence ?? 'confirmed'} />
        </div>
        {nextStep.source_type && <div className="legi-source">Zdroj: {nextStep.source_type}</div>}
      </div>
    );
  }
  if (nextStep?.label) {
    return (
      <div className="legi-next">
        <div className="legi-next__main">
          <span className="legi-next__label">{nextStep.label}</span>{' '}
          <span className="legi-next__hint">termín zatím nestanoven</span>{' '}
          <ConfidenceBadge confidence={nextStep.date_confidence ?? 'expected'} />
        </div>
      </div>
    );
  }
  return (
    <p className="muted">
      Další krok zatím neznámý <ConfidenceBadge confidence="unknown" />
    </p>
  );
}

type TimelineItem = {
  key: string;
  title: string;
  date?: string | null;
  confidence: 'confirmed' | 'expected' | 'unknown';
  muted?: boolean;
  committees?: LegislativeCommittee[];
};

function isRawOrganizationalCommitteeLabel(label: string | null | undefined): boolean {
  return !!label && label.toLocaleLowerCase('cs-CZ').includes('organizační výbor');
}

function buildTimeline(status: LegislativeStatus | null | undefined, transition: LifecycleStageTransition | null | undefined): TimelineItem[] {
  const items: TimelineItem[] = [];

  if (status?.next_step?.label || status?.next_step?.date) {
    items.push({
      key: 'next',
      title: status.next_step.label || 'Další projednání',
      date: status.next_step.date,
      confidence: status.next_step.date ? (status.next_step.date_confidence ?? 'confirmed') : (status.next_step.date_confidence ?? 'expected'),
    });
  }

  if (status) {
    items.push({
      key: 'current',
      title: status.stage_label || 'Fáze neurčena',
      date: status.entered_stage_at,
      confidence: status.entered_stage_at ? 'confirmed' : 'unknown',
      committees: status.committees,
    });
  }

  const transitionLabel = transition?.to_label || transition?.to;
  if (
    transitionLabel &&
    !isRawOrganizationalCommitteeLabel(transitionLabel) &&
    transitionLabel !== status?.stage_label &&
    transitionLabel !== status?.stage
  ) {
    items.push({
      key: 'transition',
      title: `Postup do fáze: ${transitionLabel}`,
      date: transition?.as_of_date,
      confidence: transition?.as_of_date ? 'confirmed' : 'unknown',
    });
  }

  if (status?.first_reading_done === true) {
    items.push({
      key: 'first-reading-done',
      title: '1. čtení proběhlo',
      confidence: 'confirmed',
    });
  } else if (status?.first_reading_done === false) {
    items.push({
      key: 'first-reading-pending',
      title: '1. čtení zatím neproběhlo',
      confidence: 'expected',
      muted: true,
    });
  }

  if (items.length === 0 && transitionLabel && !isRawOrganizationalCommitteeLabel(transitionLabel)) {
    items.push({
      key: 'transition-only',
      title: transitionLabel,
      date: transition?.as_of_date,
      confidence: transition?.as_of_date ? 'confirmed' : 'unknown',
    });
  }

  return items;
}

function LegislativeTimeline({
  status,
  transition,
}: {
  status: LegislativeStatus | null | undefined;
  transition?: LifecycleStageTransition | null;
}) {
  const items = buildTimeline(status, transition);
  if (items.length === 0) {
    return <p className="muted">Legislativní cesta zatím není publikovaná.</p>;
  }

  return (
    <div className="legi-timeline" aria-label="Osa legislativní cesty">
      {items.map((item, index) => (
        <div
          className={`legi-timeline__item ${index === 0 ? 'legi-timeline__item--current' : ''} ${item.muted ? 'legi-timeline__item--muted' : ''}`}
          key={item.key}
        >
          <div className="legi-timeline__rail" aria-hidden="true" />
          <div className="legi-timeline__dot" aria-hidden="true" />
          <div className="legi-timeline__body">
            <div className="legi-timeline__title">{item.title}</div>
            <div className="legi-timeline__meta">
              {item.date ? <span>{formatDate(item.date)}</span> : <span>termín zatím neznámý</span>}
              <ConfidenceBadge confidence={item.confidence} />
            </div>
            <CommitteeMiniRows committees={item.committees} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Full legislative path block for detail views. Renders nothing when there is
 * neither a published legislative_status nor a known stage transition, so older
 * cached feeds (without the additive field) stay clean.
 */
export function LegislativePath({
  status,
  transition,
}: {
  status: LegislativeStatus | null | undefined;
  transition?: LifecycleStageTransition | null;
}) {
  const hasTransition = !!(transition && (transition.from_label || transition.to_label || transition.from || transition.to));
  if (!status && !hasTransition) return null;

  const committees = status?.committees;
  const scheduled = status?.scheduled_events ?? [];

  return (
    <div className="legi-path">
      <section className="detail-panel__section legi-path__section--current">
        <h4>Legislativní cesta</h4>
        <div className="legi-phase">
          <span className="legi-phase__big">{status?.stage_label || 'Neurčeno'}</span>
          {status?.entered_stage_at && (
            <span className="legi-phase__since">od {formatDate(status.entered_stage_at)}</span>
          )}
        </div>
        <LegislativeTimeline status={status} transition={transition} />
      </section>

      {status && (
        <>
          <section className="detail-panel__section">
            <h4>Známý další krok</h4>
            <NextStepDetail nextStep={status.next_step} />
          </section>

          <section className="detail-panel__section">
            <h4>Přidělené výbory</h4>
            {committees && committees.length > 0 ? (
              <div className="committees">
                {committees.map((c) => (
                  <CommitteeRow key={`${c.code}-${c.role}`} committee={c} />
                ))}
              </div>
            ) : committees && committees.length === 0 ? (
              <p className="muted">
                Výbory zatím nejsou v publikovaném feedu připojené. Ověření proti PSP může ukázat navržený nebo
                přikázaný výbor dřív, než ho monitor publikuje strukturovaně.
              </p>
            ) : (
              <p className="muted">Přidělení výborů zatím nepublikováno.</p>
            )}
          </section>

          <section className="detail-panel__section">
            <h4>Plánované události</h4>
            {scheduled.length > 0 ? (
              <div className="legi-events">
                {scheduled.map((ev, i) => (
                  <div className="legi-event" key={`${ev.date}-${i}`}>
                    <div className="legi-event__date">{formatDate(ev.date)}</div>
                    <div className="legi-event__body">
                      <span>{ev.label || 'Jednání'}</span> <ConfidenceBadge confidence={ev.confidence} />
                      {ev.source_type && <div className="legi-source">{ev.source_type}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted">Žádné potvrzené budoucí termíny.</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
